import type { ImportMappingPreset, ColumnMappingConfig, DefaultValuesConfig } from '../types/index.js';

const STORAGE_KEY = 'insurance_tracker_import_presets';

export function createHeaderSignature(headers: string[]): string {
  return headers
    .map((h) => h.toLowerCase().trim().replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)
    .sort()
    .join('|');
}

export function getSavedPresets(): ImportMappingPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as ImportMappingPreset[];
  } catch {
    return [];
  }
}

export function savePreset(
  name: string,
  headers: string[],
  mapping: ColumnMappingConfig,
  defaultValues?: DefaultValuesConfig,
): ImportMappingPreset {
  const presets = getSavedPresets();
  const signature = createHeaderSignature(headers);
  const id = `preset_${String(Date.now())}`;

  const newPreset: ImportMappingPreset = {
    id,
    name,
    mapping,
    defaultValues,
    headerSignature: signature,
    policyTypeValueMappings: mapping.policyTypeValueMappings,
  };

  // Replace if same name already exists, otherwise add
  const existingIdx = presets.findIndex((p) => p.name.toLowerCase() === name.toLowerCase());
  if (existingIdx !== -1) {
    presets[existingIdx] = newPreset;
  } else {
    presets.unshift(newPreset);
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch (e) {
    console.error('Failed to save mapping preset to localStorage', e);
  }

  return newPreset;
}

export function deletePreset(id: string): void {
  const presets = getSavedPresets().filter((p) => p.id !== id);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch (e) {
    console.error('Failed to update mapping presets in localStorage', e);
  }
}

export function findMatchingPreset(headers: string[]): ImportMappingPreset | null {
  const currentSig = createHeaderSignature(headers);
  if (!currentSig) return null;
  const presets = getSavedPresets();
  return presets.find((p) => p.headerSignature === currentSig) ?? null;
}
